import { Divider, Grid, InputAdornment, styled } from '@material-ui/core';
import MuiToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import { AutoComplete, Dialog, Icons, TextField, Typography } from '@nutrien/cxp-components';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useState } from 'react';

import { assignInspector } from '../../graphql';
import { useMst } from '../../mobx-models/Root';
import { INewInspector, IViewInspectorDocument } from '../../models/models';
import useSelectedInspection from '../../rxdb/inspectionList/useSelectedInspection';
import useSelectedTask from '../../rxdb/taskList/useSelectedTask';
import { useRxDB } from '../../rxdb/useRxDB';
import useSelectedWpInspectors from '../../rxdb/viewInspector/useSelectedWpInspectors';
import { isJestTest } from '../../test-helpers/isJestTest';
import { mockFieldInspectorList } from '../../test-helpers/mockData/assignInspector';
import { useNotification } from '../../utilities/useNotification';
import { FieldInspectorType } from '../../utilities/enums';
import { sortByInspectorName } from '../../utilities/sortHelper';
import { isNull } from '../../utilities/utilityFunctions';
import CustomButton from '../CustomButton/CustomButton';
import StyledButton from '../InspectorInfo/StyledButton';
import useStyles from './ReAssignInspector.styles';

const ToggleButton = styled(MuiToggleButton)(({}) => ({
  '&.Mui-selected:not(.Mui-disabled), &.Mui-selected:hover:not(.Mui-disabled)': {
    color: '#121212',
    backgroundColor: 'rgba(42, 171, 63, 1)',
  },
  '&.Mui-selected, &.Mui-selected:hover': {
    color: '#121212',
    backgroundColor: 'dimgray',
  },
}));

interface Props {
  openAssignInspector: boolean;
  setOpenAssignInspector: (val: boolean) => void;
  taskId: number;
  workPackageId: number;
}

const ReAssignInspector: React.FC<Props> = ({
  openAssignInspector,
  setOpenAssignInspector,
  taskId,
  workPackageId,
}: Props) => {
  const classes = useStyles();
  const { primaryContext, CurrentUserSelected } = useMst();
  const { db } = useRxDB();
  const { successNotification, errorNotification } = useNotification();
  const { taskData, isTaskDetailLoading } = useSelectedTask(taskId);
  const { selectedInspection } = useSelectedInspection(
    String(taskData?.fkAssetInspection),
    CurrentUserSelected.workPackageId,
  );
  const { fieldInspectorsOnWorkPackage, isViewInspectorLoading } =
    useSelectedWpInspectors(workPackageId);

  const [selectedInspector, setSelectedInspector] = useState<
    IViewInspectorDocument | INewInspector
  >({} as IViewInspectorDocument | INewInspector);
  const [updatedInspector, setUpdatedInspector] = useState<{
    fieldInspectorId?: number;
    fieldInspectorName?: string;
    clerkId?: number;
    clerkName?: string;
  }>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [description, setDescription] = useState('');
  const [fieldLeadInspectorList, setFieldLeadInspectorList] = useState<IViewInspectorDocument[]>(
    [],
  );
  const [fieldInspectorList, setFieldInspectorList] = useState<IViewInspectorDocument[]>([]);

  const loggedInUser = CurrentUserSelected?.currentUser;
  const [isDECView, setIsDECView] = useState(false);
  const [inspectorType, setInspectorType] = useState<string>(FieldInspectorType.FI);
  const [disableToggleButton] = useState(false);

  const allFieldInspectorLIst = isJestTest()
    ? mockFieldInspectorList
    : isDECView
    ? primaryContext.dataEntryClerksList.toJSON()
    : primaryContext.fieldInspectorList.toJSON();

  useEffect(() => {
    if (!isViewInspectorLoading) {
      setFieldInspectorList(
        fieldInspectorsOnWorkPackage?.filter(item => {
          if (isDECView) {
            return item.clerkId;
          } else {
            return item.fieldInspectorId;
          }
        }),
      );
    }
  }, [isViewInspectorLoading, isDECView]);

  useEffect(() => {
    if (!isTaskDetailLoading) setDescription(taskData?.taskDescription);
  }, [isTaskDetailLoading]);

  const selectedFilteredInspector = useMemo(() => {
    if (isDECView) {
      return selectedInspector?.clerkId ? selectedInspector : {};
    } else if (selectedInspector?.fieldInspectorId) {
      return selectedInspector;
    }
    return {};
  }, [selectedInspector, isDECView]);

  useEffect(() => {
    const isDECViewSelected = inspectorType === FieldInspectorType.DEC;
    setIsDECView(isDECViewSelected);
    setSelectedInspector({});
  }, [inspectorType]);

  const getLeadInspectorData = () => {
    if (taskData && fieldInspectorList) {
      const leadInspectorId = taskData?.fkInspectorLead;
      const leadInspector = [...allFieldInspectorLIst].find(
        inspector => inspector.pkInspector === leadInspectorId,
      );
      if (leadInspector) {
        const flag = fieldInspectorList.find(
          inspector => inspector.fieldInspectorId === leadInspectorId,
        );
        if (!flag) {
          const clones = [
            ...fieldInspectorList,
            {
              fieldInspectorId: leadInspectorId,
              fieldInspectorName: leadInspector?.firstName + ' ' + leadInspector?.lastName,
            },
          ].map(inspector => {
            if (!isDECView && loggedInUser?.inspectorId === inspector.fieldInspectorId) {
              return {
                ...inspector,
                fieldInspectorName: 'Assigned to Me',
              };
            }
            return inspector;
          });

          setFieldInspectorList(clones);

          setFieldLeadInspectorList(clones);
        }
      }
    }
  };

  const getAssignedFieldInspector = () => {
    if (taskData && Object.keys(taskData)?.length && fieldInspectorList?.length) {
      if (
        taskData?.fkInspectorField === loggedInUser?.inspectorId ||
        taskData?.clerkId === loggedInUser?.clerkId
      ) {
        setSelectedInspector({
          fieldInspectorId: loggedInUser?.inspectorId || 0,
          fieldInspectorName: loggedInUser?.name,
          clerkId: loggedInUser?.clerkId || 0,
          clerkName: loggedInUser?.name,
        });
      } else {
        const inspectorObj = fieldLeadInspectorList.find(
          inspector => inspector.fieldInspectorId === taskData?.fkInspectorField,
        );
        if (inspectorObj) setSelectedInspector(inspectorObj);
      }
    }
  };

  const onCommentsInput = (event: React.FocusEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = event;
    setDescription(value);
  };

  const onAssign = async () => {
    setIsLoading(true);
    const isReassignmentByFI = taskData?.fkInspectorLead !== loggedInUser?.inspectorId;
    try {
      const result = await assignInspector(
        taskData?.pkAssetInspectionTask,
        taskData?.fkSchedule,
        taskData?.fkAssetInspection,
        taskData?.fkInspectorLead,
        Number(updatedInspector?.fieldInspectorId),
        description,
        taskData?.isReviewedByLI,
        taskData?.isReviewedByFI,
        taskData?.commentInspectorLead,
        taskData?.commentInspectorField,
        true,
        taskData?.proxyInspector || '',
        Number(updatedInspector?.clerkId || 0),
        taskData?.site,
        taskData?.discipline,
        taskData?.pkUuId,
        5,
        !!CurrentUserSelected?.currentUser?.clerkId,
        isNull(taskData, 'recommendations', ''),
        isNull(taskData, 'dateCompleted', null),
        isReassignmentByFI,
      );
      if (!result?.errors) {
        successNotification(
          `${!isDECView ? 'Field Inspector' : 'Data Entry Clerk'} re-assigned Successfully`,
        );
        setIsLoading(false);
        db?.startReplication();
        setOpenAssignInspector(false);
      }
    } catch (error) {
      errorNotification(
        `${!isDECView ? 'Field Inspector' : 'Data Entry Clerk'} is not updated please try again`,
      );
      setIsLoading(false);
    }
  };

  const handleToggleChange = (
    _event: React.MouseEvent<HTMLElement, MouseEvent>,
    inspType: string,
  ) => {
    if (inspType !== null) {
      setInspectorType(inspType);
    }
  };

  const handleSearchForAssign = (
    event: React.KeyboardEvent<HTMLDivElement>,
    value: IViewInspectorDocument,
  ) => {
    if (
      event.key === 'Enter' &&
      (updatedInspector?.fieldInspectorId || updatedInspector?.clerkId)
    ) {
      onAssign();
      return;
    }
    const name =
      value?.fieldInspectorId === loggedInUser?.inspectorId
        ? loggedInUser?.name
        : value?.fieldInspectorName;

    setUpdatedInspector({
      fieldInspectorId: value?.fieldInspectorId,
      fieldInspectorName: name,
      clerkId: value?.clerkId,
      clerkName: value?.clerkName,
    });
  };

  const handleOnSubmit = (event: React.KeyboardEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (updatedInspector?.fieldInspectorId || updatedInspector?.clerkId) {
      onAssign();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (
      event.key === 'Enter' &&
      (updatedInspector?.fieldInspectorId || updatedInspector?.clerkId)
    ) {
      onAssign();
    }
  };

  useEffect(() => {
    let isMounted = true;
    const getMasterData = async () => {
      try {
        if (isMounted && !isJestTest()) {
          await primaryContext.getFIListFromMobx();
          await primaryContext.getDataEntryClerksFromMobx();
        }
      } catch (e) {
        if (isMounted) {
          errorNotification('data is not loaded');
        }
      }
    };
    getMasterData();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    getLeadInspectorData();
    getAssignedFieldInspector();
  }, [taskData, allFieldInspectorLIst, fieldInspectorList]);

  const filteredFieldInspectorList = useMemo(() => {
    let filteredList = fieldInspectorList.filter(inspector => {
      if (!isDECView) {
        if (inspector?.fieldInspectorId) {
          return inspector?.fieldInspectorId !== selectedFilteredInspector?.fieldInspectorId;
        }
        return false;
      } else {
        return inspector?.clerkId
          ? inspector?.clerkId !== selectedFilteredInspector?.clerkId
          : false;
      }
    });

    filteredList = filteredList.map(inspector => {
      if (loggedInUser?.clerkId === inspector.clerkId) {
        return {
          ...inspector,
          clerkName: 'Assigned to Me',
        };
      }
      return inspector;
    });

    return filteredList;
  }, [fieldInspectorList, isDECView, selectedFilteredInspector]);

  const getDivider = () => {
    return <Divider orientation="horizontal" className={classes.divider} />;
  };

  return (
    <>
      <Dialog
        open={openAssignInspector}
        titleText={`Re-Assign ${!isDECView ? 'Inspector' : 'Data Entry Clerk'}`}
        onClose={() => setOpenAssignInspector(false)}
        backButton={false}
        dialogActions={
          <CustomButton
            isLoading={isLoading}
            onClose={onAssign}
            disabled={!updatedInspector?.fieldInspectorId && !updatedInspector?.clerkId}
            buttonText="Re-Assign"
          />
        }
        maxWidth="xs"
        noMinHeight
      >
        <form onSubmit={handleOnSubmit}>
          <Grid container className={classes.root}>
            {selectedInspection?.taskList?.length <= 1 && (
              <>
                {' '}
                <ToggleButtonGroup
                  color="#121212"
                  value={inspectorType}
                  exclusive
                  onChange={handleToggleChange}
                >
                  <ToggleButton
                    disabled={disableToggleButton}
                    value={FieldInspectorType.FI}
                    data-testid="nutrientogglebutton"
                  >
                    Field Inspector
                  </ToggleButton>
                  <ToggleButton
                    disabled={disableToggleButton}
                    value={FieldInspectorType.DEC}
                    data-testid="sptogglebutton"
                  >
                    Data Entry Clerk
                  </ToggleButton>
                </ToggleButtonGroup>
                {getDivider()}
              </>
            )}
            {selectedFilteredInspector &&
              Object.keys(selectedFilteredInspector)?.length > 0 &&
              selectedFilteredInspector?.clerkId !== loggedInUser?.clerkId && (
                <>
                  <Grid item container xs={12} alignItems="center">
                    <Grid item container alignItems="center">
                      <Typography variant="body2" className={classes.nameLabel}>
                        Assigned {!isDECView ? 'Inspector' : 'Data Entry Clerk'}
                      </Typography>
                    </Grid>
                    <Grid item container>
                      <StyledButton inspector={selectedFilteredInspector} canDelete={false} />
                    </Grid>
                  </Grid>
                  {getDivider()}
                </>
              )}

            <Grid item container>
              <Typography variant="subtitle2" className="reassignInspectorLabel">
                Assign {!isDECView ? 'Inspector' : 'Data Entry Clerk'}
              </Typography>
              <AutoComplete
                noMinHeight
                size="small"
                closeIcon=""
                startAdornment={
                  <InputAdornment position="start">
                    <Icons.SearchFeather
                      color="rgba(149, 149, 149, 1)"
                      className={classes.searchIcon}
                    />
                  </InputAdornment>
                }
                endAdornment={
                  <InputAdornment position="end">
                    <Icons.PlusCircleFeather
                      className={classes.plusIcon}
                      color="primary"
                      strokeWidth="1.5px"
                    />
                  </InputAdornment>
                }
                placeholder="Search"
                onChange={handleSearchForAssign}
                list={filteredFieldInspectorList?.sort(sortByInspectorName)}
                value={selectedInspector.clerkName || selectedInspector.fieldInspectorName}
                getOptionLabel={(option: IViewInspectorDocument) =>
                  option?.fieldInspectorName || option?.clerkName || ''
                }
                data-testid="assignInspector-id"
              />
            </Grid>

            <Grid item container className={classes.commentPadding}>
              <Typography variant="subtitle2" className="reassignInspectorLabel">
                Comments
              </Typography>
              <TextField
                defaultValue={description}
                onChange={onCommentsInput}
                onKeyDown={handleKeyDown}
                noMinHeight
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Type"
                multiline
                rows={3}
                inputProps={{
                  'data-testid': 'commentfields',
                }}
              />
            </Grid>
          </Grid>
        </form>
      </Dialog>
    </>
  );
};

export default observer(ReAssignInspector);
